import { produce } from 'immer';
import { createJSONStorage } from 'zustand/middleware';

import { StoreHelper } from '@helpers';
import { SafeCredentialTypeEnum } from '@types';

export const SMS_COUNTER_INTERVAL_SEC = 60;

export enum LoginBlock {
	Alias = 'alias',
	Multichannel = 'multichannel',
	Password = 'password',
	Safe = 'safe',
	TaxNumber = 'taxNumber',
}

export type LoginFlow =
	| 'multichannel'
	| 'multichannelWithSafe'
	| 'password'
	| 'passwordWithTaxNumber'
	| 'updateCredentials';

export type SelectedSafeChipOption = Exclude<SafeCredentialTypeEnum, SafeCredentialTypeEnum.SmsOrToken>;

export interface SafeFetchCount {
	sms?: number;
	token?: number;
}

export interface SafeState {
	fetchCount?: SafeFetchCount;
	selectedChip?: SelectedSafeChipOption;
	smsCounter?: number;
}

export interface TokenInputs {
	input1?: string;
	input2?: string;
}

export interface LoginForm {
	alias?: string;
	multichannel?: string;
	password?: string;
	taxNumber?: string;
	sms?: string;
	token?: TokenInputs;
	captchaValue?: string;
}

export interface LoginState {
	flow?: LoginFlow | '';
	form?: LoginForm;
	safe?: SafeState;
}

export const initialLoginState: LoginState = {
	flow: '',
	form: {
		alias: '',
		multichannel: '',
		password: '',
		taxNumber: '',
		sms: '',
		token: {
			input1: '',
			input2: '',
		},
		captchaValue: '',
	},
	safe: {
		fetchCount: {
			sms: 0,
			token: 0,
		},
		selectedChip: undefined,
		smsCounter: SMS_COUNTER_INTERVAL_SEC,
	},
};

interface UseLoginStoreOutput extends LoginState {
	setState: (state: LoginState) => void;
	resetState: (state: LoginState) => void;
	resetFormState: (state: LoginForm) => void;
	setSafeFetchCount: (fetchCount: SafeFetchCount) => void;
	setSafeSelectedChip: (selectedChip: SelectedSafeChipOption) => void;
	decrementSafeSmsCounter: () => void;
	resetSafeSmsCounter: () => void;
}

export const useLoginStore = StoreHelper.createStore<UseLoginStoreOutput>(
	(set) => ({
		...initialLoginState,
		setState: function (state: LoginState) {
			set(
				produce((currentState: LoginState) => ({
					...currentState,
					...state,
					form: {
						...currentState.form,
						...state.form,
						token: {
							...currentState.form?.token,
							...state.form?.token,
						},
					},
					safe: {
						...currentState.safe,
						...state.safe,
						fetchCount: {
							...currentState.safe?.fetchCount,
							...state.safe?.fetchCount,
						},
					},
				})),
				false,
				'setState'
			);
		},
		resetState: function (state: LoginState) {
			set(
				produce((currentState: LoginState) => ({
					...initialLoginState,
					...state,
					form: {
						...initialLoginState.form,
						...state.form,
						token: {
							...initialLoginState.form?.token,
							...state.form?.token,
						},
					},
					safe: {
						...initialLoginState.safe,
						...state.safe,
						fetchCount: {
							...initialLoginState.safe?.fetchCount,
							...state.safe?.fetchCount,
						},
					},
				})),
				false,
				'resetState'
			);
		},
		resetFormState: function (state: LoginForm) {
			set(
				produce((currentState: LoginState) => ({
					...currentState,
					form: {
						...initialLoginState.form,
						...state,
						token: {
							...initialLoginState.form?.token,
							...state.token,
						},
					},
				})),
				false,
				'resetFormState'
			);
		},
		setSafeFetchCount: function (fetchCount: SafeFetchCount) {
			set(
				produce((currentState: LoginState) => ({
					...currentState,
					safe: {
						...currentState.safe,
						fetchCount: {
							...currentState.safe?.fetchCount,
							...fetchCount,
						},
					},
				})),
				false,
				'setSafeFetchCount'
			);
		},
		setSafeSelectedChip: function (selectedChip: SelectedSafeChipOption) {
			set(
				produce((currentState: LoginState) => ({
					...currentState,
					safe: {
						...currentState.safe,
						selectedChip,
					},
				})),
				false,
				'setSafeSelectedChip'
			);
		},
		decrementSafeSmsCounter: function () {
			set(
				produce((currentState: LoginState) => ({
					...currentState,
					safe: {
						...currentState.safe,
						smsCounter: Number(currentState.safe?.smsCounter) - 1,
					},
				})),
				false,
				'decrementSafeSmsCounter'
			);
		},
		resetSafeSmsCounter: function () {
			set(
				produce((currentState: LoginState) => ({
					...currentState,
					safe: {
						...currentState.safe,
						smsCounter: SMS_COUNTER_INTERVAL_SEC,
					},
				})),
				false,
				'resetSafeSmsCounter'
			);
		},
	}),
	{ name: 'Login', storage: createJSONStorage(() => sessionStorage) }
);
